import { X, ArrowRight, Sparkles } from 'lucide-react'
import { useGuidanceItems, useDismissGuidance } from '@/hooks/useGuidance'
import type { SkerpaGuidanceItem, GuidancePriority, GuidanceAction } from '@ispot/shared'

interface GuidanceStripProps {
  onViewChange?: (view: string) => void
  onOpenChat?: (prompt?: string) => void
  maxItems?: number
}

const PRIORITY_DOT: Record<GuidancePriority, string> = {
  high:   'bg-danger',
  medium: 'bg-warning',
  low:    'bg-text-muted',
}

function executeAction(
  action: GuidanceAction,
  onViewChange?: (view: string) => void,
  onOpenChat?: (prompt?: string) => void
) {
  if (action.kind === 'navigate' && action.target) {
    onViewChange?.(action.target)
  } else if (action.kind === 'open_chat') {
    onOpenChat?.(action.prompt)
  }
}

interface ChipProps {
  item: SkerpaGuidanceItem
  onDismiss: (id: string) => void
  onViewChange?: (view: string) => void
  onOpenChat?: (prompt?: string) => void
}

function GuidanceChip({ item, onDismiss, onViewChange, onOpenChat }: ChipProps) {
  const primaryAction = item.actions[0]

  return (
    <div className="inline-flex items-center gap-1.5 bg-bg-card border border-border rounded-full px-3 py-1 text-sm group hover:border-border-strong transition-colors">
      <span
        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${PRIORITY_DOT[item.priority]}`}
        aria-label={`${item.priority} priority`}
      />
      <span
        className="text-text-secondary truncate max-w-[240px] cursor-pointer hover:text-text"
        title={item.description}
        onClick={() => primaryAction && executeAction(primaryAction, onViewChange, onOpenChat)}
      >
        {item.title}
      </span>
      {primaryAction && (
        <button
          type="button"
          aria-label="Go"
          onClick={() => executeAction(primaryAction, onViewChange, onOpenChat)}
          className="text-text-muted hover:text-text transition-colors flex-shrink-0"
        >
          <ArrowRight className="w-3 h-3" />
        </button>
      )}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => onDismiss(item.id)}
        className="text-text-muted hover:text-text transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  )
}

export function GuidanceStrip({ onViewChange, onOpenChat, maxItems = 5 }: GuidanceStripProps) {
  const { items, isLoading } = useGuidanceItems()
  const { mutate: dismiss } = useDismissGuidance()

  if (isLoading || items.length === 0) return null

  const visible = items.slice(0, maxItems)
  const overflow = items.length - maxItems

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted flex-shrink-0">
        <Sparkles className="w-3 h-3" />
        Next steps
      </span>
      {visible.map((item) => (
        <GuidanceChip
          key={item.id}
          item={item}
          onDismiss={dismiss}
          onViewChange={onViewChange}
          onOpenChat={onOpenChat}
        />
      ))}
      {overflow > 0 && (
        <span className="text-xs text-text-muted">+{overflow} more</span>
      )}
    </div>
  )
}
